import { cva } from 'class-variance-authority';
import {
  getCSSBorderBottomHoverWidth,
  getCSSBorderBottomWidth,
  getCSSColorBg,
  getCSSColorBorder,
  getCSSColorHoverBg,
  getCSSColorHoverBorder,
  getCSSColorHoverText,
  getCSSColorText,
  getCSSPaddingTop,
  getCSSPaddingTopHover,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import type { ThemeVariantsType } from '~/types/Utilities/ThemeVariantsType';

const classes = cva('', {
  variants: {
    color: getCSSColorText(),
    hoverColor: getCSSColorHoverText(),
    background: getCSSColorBg(),
    backgroundHover: getCSSColorHoverBg(),
    border: getCSSColorBorder(),
    borderHover: getCSSColorHoverBorder(),
    borderBottom: getCSSBorderBottomWidth(),
    borderBottomHover: getCSSBorderBottomHoverWidth(),
    paddingTop: getCSSPaddingTop(),
    paddingTopHover: getCSSPaddingTopHover(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

function removeRedundantTailwindClasses(classes: string) {
  const classList = classes.split(/\s+/); // Split the string into individual classes
  const unprefixedClasses = new Set(); // Store unprefixed classes
  const mdClasses = new Map(); // Store md:* classes
  const lgClasses = new Map(); // Store lg:* classes

  // Categorize classes
  for (const cls of classList) {
    if (cls.startsWith('md:')) {
      const baseClass = cls.slice(3); // Remove "md:" prefix
      mdClasses.set(baseClass, cls);
    } else if (cls.startsWith('lg:')) {
      const baseClass = cls.slice(3); // Remove "lg:" prefix
      lgClasses.set(baseClass, cls);
    } else {
      unprefixedClasses.add(cls);
    }
  }

  // Process md:* classes
  for (const [baseClass] of mdClasses.entries()) {
    if (unprefixedClasses.has(baseClass)) {
      // If unprefixed version exists, md:* is redundant
      mdClasses.delete(baseClass);
    }
  }

  // Process lg:* classes
  for (const [baseClass] of lgClasses.entries()) {
    if (mdClasses.has(baseClass) || unprefixedClasses.has(baseClass)) {
      // Remove lg:* if md:* exists with the same base or unprefixed exists
      lgClasses.delete(baseClass);
    }
  }

  // Combine remaining classes
  const result = [
    ...unprefixedClasses,
    ...mdClasses.values(),
    ...lgClasses.values(),
  ];

  return result.join(' ');
}

function removeDuplicateClasses(input: string): string {
  const uniqueClasses = new Set(input.split(/\s+/)); // Split by whitespace and store unique items
  return removeRedundantTailwindClasses(Array.from(uniqueClasses).join(' ')); // Convert back to string
}

export const getCSSFromTheme = (
  variants: ThemeVariantsType,
  moreClasses: string = ''
) => {
  const allClasses = `${classes(variants)} ${moreClasses}`;

  return removeDuplicateClasses(allClasses);
};
